// Packages
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Form,
  Badge,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import axios from "axios";
import {
  BsDownload,
  BsPencilFill,
  BsTrashFill,
  BsArrowRepeat,
  BsPlusLg,
} from "react-icons/bs";
// Utils
import {
  AGE_LIMITS,
  calculateAge,
  catchHandler,
  getRequestHeaders,
  getUserRole,
  userRoles,
} from "../utils";
// Components
import ConfirmationModal from "../components/ConfirmationModal";
// Styling
import "./Documents.css";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_B = MAX_FILE_SIZE_MB * 1024 * 1024;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/heif",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const ACCEPT_ATTR =
  "application/pdf,image/*,.doc,.docx,.xls,.xlsx,.heic,.heif";

// Split a filename into editable base and locked extension. The extension is
// the last dotted segment (e.g. "report.v2.docx" -> base "report.v2", ext
// ".docx"). Files with no extension or a leading-dot dotfile name keep the
// whole name as base and an empty ext.
const splitName = (name) => {
  if (!name) return { base: "", ext: "" };
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) return { base: name, ext: "" };
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot) };
};

const validateFile = (file) => {
  if (!file) return "Lipsa fisier";
  const { type, size } = file;
  if (!ALLOWED_MIME_TYPES.has((type || "").toLowerCase())) {
    return "Tip de fisier neacceptat. Permise: PDF, imagini, Word, Excel.";
  }
  if (size > MAX_FILE_SIZE_B) {
    return `Poti incarca doar fisiere de maxim ${MAX_FILE_SIZE_MB}Mb`;
  }
  return null;
};

const Documents = React.memo((props) => {
  const params = useParams();
  const history = useHistory();
  const catchHandlerBinded = catchHandler.bind(null, history);

  // Resolve effective userId: route param (admin view) wins; otherwise we
  // fall back to props.user._id (if PrivateRoute ever starts injecting it)
  // and finally to a self-fetched id from /api/users/get-profile.
  const routeUserId = params?.userId;
  const propSelfId = props?.user?._id;
  const [selfId, setSelfId] = useState(null);
  const userId = routeUserId || propSelfId || selfId;

  // isAdminView is true when the URL carries a userId param and the current
  // user is privileged. We use props.user.role first (passed from PrivateRoute
  // wrapper, when applicable) but fall back to localStorage via the userRoles
  // utility helpers (see Profile.js).
  const currentUserRole = useMemo(
    () => props?.user?.role || getUserRole(),
    [props?.user?.role]
  );

  const isAdminView = useMemo(
    () =>
      !!routeUserId &&
      (currentUserRole === userRoles.ADMIN ||
        currentUserRole === userRoles.EMPLOYEE),
    [routeUserId, currentUserRole]
  );

  const canDelete = isAdminView; // only admins/employees can delete

  // --- Data state ---
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [data, setData] = useState({
    user: null,
    identityCard: null,
    notarialAgreement: null,
    bucket: [],
    usage: { files: 0, bytes: 0, fileLimit: 10, byteLimit: 20 * 1024 * 1024 },
  });

  // --- Refs (hidden file inputs) ---
  const ciInputRef = useRef(null);
  const notarialInputRef = useRef(null);
  const bucketReplaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null); // holds fileId being replaced

  // --- Modal states ---
  const [addModal, setAddModal] = useState({
    isOpen: false,
    file: null,
    description: "",
  });
  const [editModal, setEditModal] = useState({
    isOpen: false,
    file: null, // the bucket item being edited
    base: "", // editable filename without extension
    ext: "", // locked extension (e.g. ".pdf"); never edited via the UI
    description: "",
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // "bucket" | "notarial"
    file: null,
  });

  // --- Computed values ---
  const ageInMonths = useMemo(() => {
    const birthDate = data?.user?.birthDate;
    if (!birthDate) return null;
    const [years, months] = calculateAge(birthDate);
    return years * 12 + months;
  }, [data?.user?.birthDate]);

  const isNotarialAgeRange = useMemo(() => {
    if (ageInMonths === null) return false;
    return (
      ageInMonths >= AGE_LIMITS.UNDER && ageInMonths < AGE_LIMITS.MINOR
    );
  }, [ageInMonths]);

  const showNotarialRow = useMemo(
    () => Boolean(data?.notarialAgreement) || isNotarialAgeRange,
    [data?.notarialAgreement, isNotarialAgeRange]
  );

  const usageBytesMB = useMemo(() => {
    const bytes = data?.usage?.bytes || 0;
    return Math.round((bytes / (1024 * 1024)) * 10) / 10;
  }, [data?.usage?.bytes]);

  const usageByteLimitMB = useMemo(() => {
    const bytes = data?.usage?.byteLimit || 20 * 1024 * 1024;
    return Math.round(bytes / (1024 * 1024));
  }, [data?.usage?.byteLimit]);

  const reachedFileLimit = useMemo(
    () =>
      (data?.usage?.files || 0) >= (data?.usage?.fileLimit || 10),
    [data?.usage?.files, data?.usage?.fileLimit]
  );

  // --- Data fetch ---
  const fetchData = useCallback((initial = false) => {
    // Without a resolved userId we stay in loading state; the selfId-resolution
    // effect will re-trigger this once it lands.
    if (!userId) return;

    if (initial) setLoading(true);

    axios
      .get(`/api/user-documents/${userId}`, getRequestHeaders())
      .then((res) => {
        const { data: responseData = {} } = res;
        setData({
          user: responseData.user || null,
          identityCard: responseData.identityCard || null,
          notarialAgreement: responseData.notarialAgreement || null,
          bucket: responseData.bucket || [],
          usage:
            responseData.usage || {
              files: 0,
              bytes: 0,
              fileLimit: 10,
              byteLimit: 20 * 1024 * 1024,
            },
        });
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        if (initial) setLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Self-mode userId resolution: if we have no route param and PrivateRoute
  // did not inject a user prop, fetch the current user's profile to get _id.
  useEffect(() => {
    if (routeUserId || propSelfId || selfId) return;
    axios
      .get(`/api/users/get-profile`, getRequestHeaders())
      .then((res) => {
        const { data: profile = {} } = res;
        if (profile?._id) setSelfId(profile._id);
      })
      .catch(catchHandlerBinded);
  }, [routeUserId, propSelfId, selfId]);

  // --- Download helper (blob + anchor download) ---
  const downloadBlob = useCallback(
    (url, filename) => {
      setActionLoading(true);
      axios
        .get(url, getRequestHeaders({ requestDownloadFilesFlag: true }))
        .then((res) => {
          const type = res.headers["content-type"];
          const blob = new Blob([res.data], { type, encoding: "UTF-8" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename || "document";
          link.click();
          link.remove();
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setActionLoading(false);
        });
    },
    [catchHandlerBinded]
  );

  const onDownloadIdentityCard = useCallback(() => {
    if (!data?.identityCard) return;
    downloadBlob(
      `/api/user-documents/${userId}/identity-card/download`,
      data.identityCard.originalname || "carte-identitate"
    );
  }, [downloadBlob, userId, data?.identityCard]);

  const onDownloadNotarial = useCallback(() => {
    if (!data?.notarialAgreement) return;
    downloadBlob(
      `/api/user-documents/${userId}/notarial-agreement/download`,
      data.notarialAgreement.originalname || "acord-notarial"
    );
  }, [downloadBlob, userId, data?.notarialAgreement]);

  const onDownloadBucket = useCallback(
    (fileId, originalname) => {
      downloadBlob(
        `/api/user-documents/${userId}/bucket/${fileId}/download`,
        originalname || "document"
      );
    },
    [downloadBlob, userId]
  );

  // --- CI / Notarial upload ---
  const triggerCiInput = useCallback(() => {
    if (ciInputRef.current) ciInputRef.current.click();
  }, []);

  const triggerNotarialInput = useCallback(() => {
    if (notarialInputRef.current) notarialInputRef.current.click();
  }, []);

  const uploadSpecialDoc = useCallback(
    (notarialFlag, file) => {
      const err = validateFile(file);
      if (err) {
        toast.warning(err);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const url = notarialFlag
        ? `/api/user-documents/${userId}/notarial-agreement`
        : `/api/user-documents/${userId}/identity-card`;

      setActionLoading(true);
      axios
        .patch(
          url,
          formData,
          getRequestHeaders({ requestHasFilesFlag: true })
        )
        .then(() => {
          toast.success(
            notarialFlag
              ? "Acordul notarial a fost salvat cu succes"
              : "Cartea de identitate a fost salvata cu succes"
          );
          fetchData();
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setActionLoading(false);
        });
    },
    [userId, fetchData, catchHandlerBinded]
  );

  const onCiInputChange = useCallback(
    (event) => {
      const { target = {} } = event;
      const { files } = target;
      if (!files?.length) return;
      uploadSpecialDoc(false, files[0]);
      // reset so re-selecting the same file fires onChange again
      event.target.value = "";
    },
    [uploadSpecialDoc]
  );

  const onNotarialInputChange = useCallback(
    (event) => {
      const { target = {} } = event;
      const { files } = target;
      if (!files?.length) return;
      uploadSpecialDoc(true, files[0]);
      event.target.value = "";
    },
    [uploadSpecialDoc]
  );

  // --- Bucket: Add ---
  const toggleAddModal = useCallback(() => {
    setAddModal((prev) =>
      prev.isOpen
        ? { isOpen: false, file: null, description: "" }
        : { isOpen: true, file: null, description: "" }
    );
  }, []);

  const onAddFileChange = useCallback((event) => {
    const { target = {} } = event;
    const { files } = target;
    if (!files?.length) {
      setAddModal((prev) => ({ ...prev, file: null }));
      return;
    }
    const file = files[0];
    const err = validateFile(file);
    if (err) {
      toast.warning(err);
      event.target.value = "";
      setAddModal((prev) => ({ ...prev, file: null }));
      return;
    }
    setAddModal((prev) => ({ ...prev, file }));
  }, []);

  const onAddDescriptionChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setAddModal((prev) => ({ ...prev, description: value }));
  }, []);

  const submitAdd = useCallback(() => {
    if (!addModal.file) {
      toast.warning("Selecteaza un fisier");
      return;
    }
    if (reachedFileLimit) {
      toast.warning("Ai atins limita de documente.");
      return;
    }

    const formData = new FormData();
    formData.append("file", addModal.file);
    if (addModal.description) {
      formData.append("description", addModal.description);
    }

    setActionLoading(true);
    axios
      .post(
        `/api/user-documents/${userId}/bucket`,
        formData,
        getRequestHeaders({ requestHasFilesFlag: true })
      )
      .then(() => {
        toast.success("Documentul a fost adaugat cu succes");
        setAddModal({ isOpen: false, file: null, description: "" });
        fetchData();
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setActionLoading(false);
      });
  }, [
    addModal,
    reachedFileLimit,
    userId,
    fetchData,
    catchHandlerBinded,
  ]);

  // --- Bucket: Edit (name + description, no file) ---
  const openEditModal = useCallback((file) => {
    const { base, ext } = splitName(file.originalname || "");
    setEditModal({
      isOpen: true,
      file,
      base,
      ext,
      description: file.description || "",
    });
  }, []);

  const toggleEditModal = useCallback(() => {
    setEditModal((prev) =>
      prev.isOpen
        ? { isOpen: false, file: null, base: "", ext: "", description: "" }
        : prev
    );
  }, []);

  const onEditNameChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setEditModal((prev) => ({ ...prev, base: value }));
  }, []);

  const onEditDescriptionChange = useCallback((event) => {
    const { target = {} } = event;
    const { value } = target;
    setEditModal((prev) => ({ ...prev, description: value }));
  }, []);

  const submitEdit = useCallback(() => {
    if (!editModal.file) return;
    const fileId = editModal.file._id;

    // Recompose the full filename. The extension was locked when the modal
    // opened, so the user cannot change it through this flow.
    const originalname = `${(editModal.base || "").trim()}${editModal.ext}`;

    const body = {
      originalname,
      description: editModal.description,
    };

    setActionLoading(true);
    axios
      .patch(
        `/api/user-documents/${userId}/bucket/${fileId}`,
        body,
        getRequestHeaders()
      )
      .then(() => {
        toast.success("Documentul a fost modificat cu succes");
        setEditModal({
          isOpen: false,
          file: null,
          base: "",
          ext: "",
          description: "",
        });
        fetchData();
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setActionLoading(false);
      });
  }, [editModal, userId, fetchData, catchHandlerBinded]);

  // --- Bucket: Replace (file replacement) ---
  const triggerBucketReplace = useCallback((fileId) => {
    replaceTargetRef.current = fileId;
    if (bucketReplaceInputRef.current) {
      bucketReplaceInputRef.current.click();
    }
  }, []);

  const onBucketReplaceChange = useCallback(
    (event) => {
      const { target = {} } = event;
      const { files } = target;
      const fileId = replaceTargetRef.current;
      replaceTargetRef.current = null;

      if (!files?.length || !fileId) {
        if (event.target) event.target.value = "";
        return;
      }
      const file = files[0];
      const err = validateFile(file);
      if (err) {
        toast.warning(err);
        event.target.value = "";
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      setActionLoading(true);
      axios
        .patch(
          `/api/user-documents/${userId}/bucket/${fileId}`,
          formData,
          getRequestHeaders({ requestHasFilesFlag: true })
        )
        .then(() => {
          toast.success("Documentul a fost inlocuit cu succes");
          fetchData();
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setActionLoading(false);
          if (event.target) event.target.value = "";
        });
    },
    [userId, fetchData, catchHandlerBinded]
  );

  // --- Delete (Bucket + Notarial) ---
  const openDeleteModal = useCallback((file) => {
    setDeleteModal({ isOpen: true, type: "bucket", file });
  }, []);

  const openDeleteNotarialModal = useCallback(() => {
    setDeleteModal({ isOpen: true, type: "notarial", file: null });
  }, []);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModal((prev) =>
      prev.isOpen ? { isOpen: false, type: null, file: null } : prev
    );
  }, []);

  const submitDelete = useCallback(() => {
    if (deleteModal.type === "notarial") {
      setActionLoading(true);
      axios
        .delete(
          `/api/user-documents/${userId}/notarial-agreement`,
          getRequestHeaders()
        )
        .then(() => {
          toast.success("Acordul notarial a fost sters cu succes");
          setDeleteModal({ isOpen: false, type: null, file: null });
          fetchData();
        })
        .catch(catchHandlerBinded)
        .finally(() => {
          setActionLoading(false);
        });
      return;
    }

    if (!deleteModal.file) return;
    const fileId = deleteModal.file._id;

    setActionLoading(true);
    axios
      .delete(
        `/api/user-documents/${userId}/bucket/${fileId}`,
        getRequestHeaders()
      )
      .then(() => {
        toast.success("Documentul a fost sters cu succes");
        setDeleteModal({ isOpen: false, type: null, file: null });
        fetchData();
      })
      .catch(catchHandlerBinded)
      .finally(() => {
        setActionLoading(false);
      });
  }, [deleteModal, userId, fetchData, catchHandlerBinded]);

  // --- Render helpers ---
  const renderTitle = () => {
    const name = data?.user?.name || data?.user?.email || "";
    const membershipId = data?.user?.membershipId;
    const paddedId =
      membershipId !== null && membershipId !== undefined
        ? String(membershipId).padStart(7, "0")
        : "";

    return (
      <h2 className="mb-4">
        Dosar cursant
        {name ? ` - ${name}` : ""}
        {paddedId ? ` (ID: ${paddedId})` : ""}
      </h2>
    );
  };

  const renderCiRow = () => {
    const ci = data?.identityCard;
    return (
      <div className="doc-row" key="ci-row">
        <div className="doc-row-info">
          <div className="doc-row-name">
            Carte de identitate
            {!ci && (
              <Badge color="danger" pill className="ms-2">
                Obligatoriu - lipsa
              </Badge>
            )}
          </div>
          {ci ? (
            <div className="doc-row-meta">
              <span>{ci.originalname || "Fisier incarcat"}</span>
              <span> &middot; incarcat la {formatDate(ci.createdAt)}</span>
            </div>
          ) : (
            <div className="doc-row-meta">
              Te rugam sa incarci cartea de identitate.
            </div>
          )}
        </div>
        <div className="doc-row-actions">
          {ci ? (
            <>
              <Button
                color="info"
                size="sm"
                disabled={actionLoading}
                onClick={onDownloadIdentityCard}
                title="Descarca"
              >
                <BsDownload /> Descarca
              </Button>
              <Button
                color="warning"
                size="sm"
                disabled={actionLoading}
                onClick={triggerCiInput}
                title="Inlocuieste"
              >
                <BsArrowRepeat /> Inlocuieste
              </Button>
            </>
          ) : (
            <Button
              color="primary"
              size="sm"
              disabled={actionLoading}
              onClick={triggerCiInput}
            >
              Incarcare
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderNotarialRow = () => {
    if (!showNotarialRow) return null;
    const notarial = data?.notarialAgreement;

    return (
      <div className="doc-row" key="notarial-row">
        <div className="doc-row-info">
          <div className="doc-row-name">
            Acord notarial
            {!notarial && (
              <Badge color="danger" pill className="ms-2">
                Obligatoriu - lipsa
              </Badge>
            )}
          </div>
          {notarial ? (
            <div className="doc-row-meta">
              <span>{notarial.originalname || "Fisier incarcat"}</span>
              <span>
                {" "}
                &middot; incarcat la {formatDate(notarial.createdAt)}
              </span>
            </div>
          ) : (
            <div className="doc-row-meta">
              Te rugam sa incarci acordul notarial.
            </div>
          )}
        </div>
        <div className="doc-row-actions">
          {notarial ? (
            <>
              <Button
                color="info"
                size="sm"
                disabled={actionLoading}
                onClick={onDownloadNotarial}
              >
                <BsDownload /> Descarca
              </Button>
              <Button
                color="warning"
                size="sm"
                disabled={actionLoading}
                onClick={triggerNotarialInput}
              >
                <BsArrowRepeat /> Inlocuieste
              </Button>
              {canDelete && (
                <Button
                  color="danger"
                  size="sm"
                  disabled={actionLoading}
                  onClick={openDeleteNotarialModal}
                  title="Sterge"
                >
                  <BsTrashFill /> Sterge
                </Button>
              )}
            </>
          ) : (
            <Button
              color="primary"
              size="sm"
              disabled={actionLoading}
              onClick={triggerNotarialInput}
            >
              Incarcare
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderBucketRows = () => {
    const bucket = data?.bucket || [];
    if (!bucket.length) {
      return (
        <tr>
          <td colSpan={4} className="doc-empty">
            Niciun document incarcat
          </td>
        </tr>
      );
    }
    return bucket.map((file) => (
      <tr key={`bucket-row-${file._id}`}>
        <td>{file.originalname || "-"}</td>
        <td>{file.description || "-"}</td>
        <td>{formatDate(file.createdAt)}</td>
        <td className="text-end">
          <div className="doc-row-actions" style={{ justifyContent: "flex-end" }}>
            <Button
              color="info"
              size="sm"
              disabled={actionLoading}
              onClick={() =>
                onDownloadBucket(file._id, file.originalname)
              }
              title="Descarca"
            >
              <BsDownload />
            </Button>
            <Button
              color="secondary"
              size="sm"
              disabled={actionLoading}
              onClick={() => openEditModal(file)}
              title="Editeaza nume / descriere"
            >
              <BsPencilFill />
            </Button>
            <Button
              color="warning"
              size="sm"
              disabled={actionLoading}
              onClick={() => triggerBucketReplace(file._id)}
              title="Inlocuieste fisierul"
            >
              <BsArrowRepeat />
            </Button>
            {canDelete && (
              <Button
                color="danger"
                size="sm"
                disabled={actionLoading}
                onClick={() => openDeleteModal(file)}
                title="Sterge"
              >
                <BsTrashFill />
              </Button>
            )}
          </div>
        </td>
      </tr>
    ));
  };

  // --- Render ---
  return (
    <Container className="mt-5 mb-5 documents-page" style={{ maxWidth: "95%" }}>
      {/* Hidden file inputs for CI / Notarial / Bucket replacement */}
      <Input
        innerRef={ciInputRef}
        type="file"
        name="file"
        accept={ACCEPT_ATTR}
        className="doc-hidden-input"
        onChange={onCiInputChange}
      />
      <Input
        innerRef={notarialInputRef}
        type="file"
        name="file"
        accept={ACCEPT_ATTR}
        className="doc-hidden-input"
        onChange={onNotarialInputChange}
      />
      <Input
        innerRef={bucketReplaceInputRef}
        type="file"
        name="file"
        accept={ACCEPT_ATTR}
        className="doc-hidden-input"
        onChange={onBucketReplaceChange}
      />

      <Row>
        <Col sm="12" lg={{ size: 10, offset: 1 }}>
          <Card className="p-4 bg-light shadow-sm">
            <CardBody>
              {loading ? (
                <div className="text-center p-5">
                  <Spinner
                    color="dark"
                    style={{ width: "4rem", height: "4rem" }}
                  />
                </div>
              ) : !data?.user ? (
                <div className="doc-empty">
                  Nu am putut incarca informatiile utilizatorului.
                </div>
              ) : (
                <>
                  {renderTitle()}

                  {/* Section: Mandatory documents */}
                  <div className="doc-section">
                    <h4 className="doc-section-title">
                      Documente obligatorii
                    </h4>
                    {renderCiRow()}
                    {renderNotarialRow()}
                  </div>

                  {/* Section: Bucket */}
                  <div className="doc-section">
                    <h4 className="doc-section-title">Alte documente</h4>
                    <div className="doc-bucket-toolbar">
                      <Badge
                        color="secondary"
                        className="doc-usage-badge"
                        pill
                      >
                        {data?.usage?.files || 0}/
                        {data?.usage?.fileLimit || 10} documente &middot;{" "}
                        {usageBytesMB} MB / {usageByteLimitMB} MB
                      </Badge>
                      <Button
                        color="primary"
                        size="sm"
                        disabled={actionLoading || reachedFileLimit}
                        onClick={toggleAddModal}
                      >
                        <BsPlusLg /> Adauga document
                      </Button>
                    </div>
                    <Table hover responsive className="doc-table">
                      <thead>
                        <tr>
                          <th>Nume</th>
                          <th>Descriere</th>
                          <th>Incarcat la</th>
                          <th className="text-end">Actiuni</th>
                        </tr>
                      </thead>
                      <tbody>{renderBucketRows()}</tbody>
                    </Table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add modal */}
      <Modal isOpen={addModal.isOpen} toggle={toggleAddModal} centered>
        <ModalHeader toggle={toggleAddModal}>Adauga document</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="add-doc-file">Fisier (PDF sau imagine, max {MAX_FILE_SIZE_MB}MB)</Label>
              <Input
                id="add-doc-file"
                type="file"
                name="file"
                accept={ACCEPT_ATTR}
                onChange={onAddFileChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="add-doc-description">Descriere (optional)</Label>
              <Input
                id="add-doc-description"
                type="textarea"
                value={addModal.description}
                onChange={onAddDescriptionChange}
                rows={3}
                maxLength={500}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={submitAdd}
            disabled={actionLoading || !addModal.file}
          >
            Salveaza
          </Button>
          <Button
            color="secondary"
            onClick={toggleAddModal}
            disabled={actionLoading}
          >
            Anuleaza
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit modal */}
      <Modal isOpen={editModal.isOpen} toggle={toggleEditModal} centered>
        <ModalHeader toggle={toggleEditModal}>Editare document</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="edit-doc-name">Nume fisier</Label>
              <InputGroup>
                <Input
                  id="edit-doc-name"
                  type="text"
                  value={editModal.base}
                  onChange={onEditNameChange}
                  maxLength={255}
                />
                {editModal.ext ? (
                  <InputGroupText>{editModal.ext}</InputGroupText>
                ) : null}
              </InputGroup>
            </FormGroup>
            <FormGroup>
              <Label for="edit-doc-description">Descriere</Label>
              <Input
                id="edit-doc-description"
                type="textarea"
                value={editModal.description}
                onChange={onEditDescriptionChange}
                rows={3}
                maxLength={500}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={submitEdit}
            disabled={actionLoading}
          >
            Salveaza
          </Button>
          <Button
            color="secondary"
            onClick={toggleEditModal}
            disabled={actionLoading}
          >
            Anuleaza
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        toggle={toggleDeleteModal}
        onConfirm={submitDelete}
        title="Confirmare stergere"
        message={
          deleteModal.type === "notarial"
            ? "Sigur doriți să ștergeți acordul notarial?"
            : deleteModal.file
            ? `Esti sigur ca vrei sa stergi documentul "${
                deleteModal.file.originalname || ""
              }"? Aceasta operatie este ireversibila.`
            : ""
        }
        confirmText="Da, sterge"
        cancelText="Anuleaza"
        confirmColor="danger"
        loading={actionLoading}
      />
    </Container>
  );
});

export default Documents;
